"use client"

import React from "react"
import AppIcon from "@/app/components/atomic/AppIcon"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import {PRETTO_UTM_CONTENT, PRETTO_UTM_TERM} from "@/app/constants/prettoJourney"
import {useTracking} from "@/app/contexts/tracking-context"
import {useAffiliatedLink} from "@/app/(pages)/taux-pret-immobilier/hooks/useAffiliatedLink"

interface Props {
    featuredRates: Array<{
        duration: number
        interestRate: string | number
        evolutionClass: string
    }>
    isVariant: boolean
    isLoading: boolean
}

export default function LoanRateComparatorFeaturedRates({featuredRates, isLoading, isVariant}: Props) {
    const {track} = useTracking()
    const linkHref = useAffiliatedLink(isVariant, {content: PRETTO_UTM_CONTENT.FEATURED_RATES, term: PRETTO_UTM_TERM.TAUX_PRET})
    const shouldOpenInNewTab = !isVariant

    const trackButtonClick = () => {
        const eventName = isVariant
            ? "gtm_taux_pret_immo_ab_test_bank_click"
            : "gtm_partner_link_clic"

        track({
            event: eventName,
            event_source: "featured_rates_cta",
            partner_url: linkHref,
        })
    }

    return (
        <div
            className="flex flex-col overflow-hidden z-11"
            data-test="loan-rate-featured-rates"
        >
            {(isLoading || !featuredRates || featuredRates.length === 0) && <FeaturedRatesSkeleton />}
            {!isLoading && featuredRates && featuredRates.length > 0 && (
                <FeaturedRatesSection>
                    {featuredRates.map((rateData) => (
                        <FeaturedRateCard
                            key={rateData.duration}
                            duration={rateData.duration}
                            interestRate={rateData?.interestRate ?? "x,xx"}
                            evolutionClass={rateData?.evolutionClass ?? ""}
                        />
                    ))}
                </FeaturedRatesSection>
            )}

            <FeaturedCTASection
                linkHref={linkHref}
                shouldOpenInNewTab={shouldOpenInNewTab}
                onTrack={trackButtonClick}
            />
        </div>
    )
}

function FeaturedCTASection({
                                linkHref,
                                shouldOpenInNewTab,
                                onTrack,
                            }: {
    linkHref: string
    shouldOpenInNewTab: boolean
    onTrack: () => void
}) {
    return (
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 px-6 py-5 bg-pastel-orange/20 rounded-2xl">
            <span className="hidden lg:flex text-lg font-semibold text-pale">
                Simulez votre prêt immobilier en ligne grâce au courtier Pretto
            </span>

            <span className="hidden lg:flex">
                <AppButton
                    href={linkHref}
                    target={shouldOpenInNewTab ? "_blank" : "_self"}
                    onClick={onTrack}
                    theme="orange"
                    size="sm"
                    arrowPosition="before"
                    dataTest="featured-rates-cta"
                >
                    Simulation en ligne
                </AppButton>
            </span>

            <span className="flex lg:hidden w-full justify-center">
                <AppButton
                    href={linkHref}
                    target={shouldOpenInNewTab ? "_blank" : "_self"}
                    onClick={onTrack}
                    theme="orange"
                    size="sm"
                    arrowPosition="before"
                    wFull
                    dataTest="featured-rates-cta-mobile"
                >
                    J'obtiens le meilleur taux
                </AppButton>
            </span>
        </div>
    )
}

function FeaturedRateCard({
                              duration,
                              interestRate,
                              evolutionClass,
                          }: {
    duration: number
    interestRate: string | number
    evolutionClass: string
}) {
    return (
        <div
            className="flex flex-col items-start gap-2 rounded-2xl bg-white shadow p-4 lg:p-6 flex-1"
            data-test="featured-rate-card"
        >
            <span className="text-xs font-medium uppercase tracking-wide text-pale/70 lg:text-sm">
                <span className="hidden lg:inline">Durée du prêt : {duration} ans</span>
                <span className="inline lg:hidden whitespace-nowrap">Prêt sur {duration} ans</span>
            </span>

            <strong
                className="text-2xl lg:text-4xl font-extrabold text-rm-blue-deep flex items-center gap-3"
                data-test="featured-rate-percentage"
            >
                {interestRate}%
                <span className="hidden lg:flex">
                    <AppIcon iconClassName={evolutionClass} type="duotone" size="4xl" />
                </span>
                <span className="flex lg:hidden">
                    <AppIcon iconClassName={evolutionClass} type="duotone" size="lg" />
                </span>
            </strong>
        </div>
    )
}

function FeaturedRatesSection({children}: { children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-4 lg:gap-6 py-4 lg:py-8">
            <span className="flex text-rm-22 lg:text-3xl text-pale font-semibold">
                Les meilleurs taux <span className="hidden lg:inline">&nbsp;d'intérêt</span>
            </span>
            <div className="flex flex-col md:flex-row gap-3 lg:gap-6">
                {children}
            </div>
        </div>
    )
}

export function FeaturedRatesSkeleton() {
    const durationsToDisplayRatesFor = [15, 20, 25]

    return (
        <FeaturedRatesSection>
            {durationsToDisplayRatesFor.map((duration) => (
                <FeaturedRateCard
                    key={duration}
                    duration={duration}
                    interestRate={"x,xx"}
                    evolutionClass={"fa-arrow-circle-right text-yellow-500"}
                />
            ))}
        </FeaturedRatesSection>
    )
}
